import { useState } from 'react'
import { cn } from '@/lib/utils'

interface ProductImageProps {
  /** Image URL */
  src?: string | null
  /** Product name for alt text and fallback initial */
  name: string
  /** Optional additional class names */
  className?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * ProductImage - Reusable component for displaying product images
 * Shows a square image with rounded corners, or a fallback with the first letter
 * of the product name when no image is available or fails to load
 */
export function ProductImage({
  src,
  name,
  className,
  size = 'md',
}: ProductImageProps) {
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const showFallback = !src || imageError || !imageLoaded

  // Size variants
  const sizeClasses = {
    sm: 'h-6 w-6',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
  }

  const textSizeClasses = {
    sm: 'text-[10px]',
    md: 'text-xs',
    lg: 'text-sm',
  }

  return (
    <div
      className={cn(
        'relative flex flex-shrink-0 items-center',
        'justify-center overflow-hidden rounded-md border',
        'border-[#D4D4D4] bg-[#F5F5F5]',
        sizeClasses[size],
        className
      )}
    >
      {showFallback ? (
        <span
          className={cn('font-medium text-[#737373]', textSizeClasses[size])}
        >
          {name.charAt(0).toUpperCase()}
        </span>
      ) : (
        src && (
          <img
            alt={name}
            className="h-full w-full object-cover"
            onError={() => setImageError(true)}
            onLoad={() => setImageLoaded(true)}
            src={src}
          />
        )
      )}
      {src && !imageLoaded && !imageError && (
        <img
          alt=""
          className="hidden"
          onError={() => setImageError(true)}
          onLoad={() => setImageLoaded(true)}
          src={src}
        />
      )}
    </div>
  )
}
